import { useMemo } from "react";
import { NetworkStatus } from "@apollo/client";
import { keyBy } from "lodash";
import clsx from "clsx";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { SiteStage } from "react-migration/domains/sites/typings/SiteStage";
import { Select } from "react-migration/components/Select";
import { Button } from "react-migration/components/Button";

import { NO_SELECTION } from "./useCheckStages/useCheckStages";

export interface StageSelectProps {
  value: string;
  onChange: (id: string) => void;
  stages: SiteStage[];
  loading: boolean;
  networkStatus: NetworkStatus;
  size?: "xs" | "sm";
  withStageColor?: boolean;
  alwaysDropdown?: boolean;
}

export function StageSelect({
  value,
  onChange,
  stages,
  networkStatus,
  loading,
  withStageColor = false,
  alwaysDropdown = false,
}: StageSelectProps) {
  const { t } = useTranslation();
  const stagesById = useMemo(() => stages && keyBy(stages, "_id"), [stages]);
  const hasNoStages = networkStatus === NetworkStatus.ready && !stages?.length;

  return (
    <div className="flex flex-col overflow-hidden gap-y-1 grow" data-testid="stage-select">
      <div className={clsx(alwaysDropdown ? "block" : "hidden md:block")}>
        <Select
          value={value}
          disabled={loading || hasNoStages}
          onValueChange={onChange}
          data-testid="stage-select-dropdown"
          size={Select.Size.XS}
        >
          <Select.Option value={NO_SELECTION} disabled hidden>
            {hasNoStages
              ? t("sites.save_site.save_site_dropdown.create_stage_prompt")
              : t("sites.save_site.save_site_dropdown.select_stage_prompt")}
          </Select.Option>
          {(stages || []).map(({ _id, name, color }) => (
            <Select.Option key={_id} value={_id} title={name}>
              <div className="flex items-center justify-start gap-x-2">
                {withStageColor && <div className="w-4 h-4 rounded-full" style={{ backgroundColor: color }} />}
                {`${name} ${stagesById[_id]?.hidden ? t("sites.save_site.save_site_dropdown.hidden") : ""}`}
              </div>
            </Select.Option>
          ))}
        </Select>
      </div>
      <div
        className={clsx(
          "overflow-y-auto max-h-[50vh] divide-y divide-neutral-200",
          alwaysDropdown ? "hidden" : "block md:hidden "
        )}
      >
        {(stages || []).map(({ _id, name }) => (
          <div
            key={_id}
            onClick={() => onChange(_id)}
            className={clsx("text-sm px-2 py-3 rounded-sm", value === _id ? "bg-platform-200" : "")}
          >
            {name} {stagesById[_id]?.hidden && t("sites.save_site.save_site_dropdown.hidden")}
          </div>
        ))}
      </div>
    </div>
  );
}

interface StageSelectPopoverContentProps {
  selectedStageId: string;
  stages?: SiteStage[];
  loading: boolean;
  networkStatus: NetworkStatus;
  saveHandler(): void;
  closeHandler(): void;
  setSelectedStageId(id: string): void;
}

const EMPTY_ARRAY: SiteStage[] = [];

export function StageSelectPopoverContent({
  selectedStageId,
  stages = EMPTY_ARRAY,
  loading,
  networkStatus,
  setSelectedStageId,
  closeHandler,
  saveHandler,
}: StageSelectPopoverContentProps) {
  const { t } = useTranslation();

  return (
    <>
      <StageSelect
        value={selectedStageId}
        onChange={setSelectedStageId}
        stages={stages}
        loading={loading}
        networkStatus={networkStatus}
      />
      <div className="flex justify-between">
        <Button onClick={closeHandler} variant="tertiary">
          {t("sidebar.react.saved_search_sidebar.cancel")}
        </Button>
        <Button variant="primary" onClick={saveHandler} data-testid="save-site-inner">
          {t("sidebar.react.saved_search_sidebar.save")}
        </Button>
      </div>
    </>
  );
}
